// MARK://dhk8/admin-shell/App
// Router for the NintaiGO merchant admin. Public routes: /login, /signup, /onboarding.
// Everything else lives behind RequireAuth inside the AdminLayout shell. The
// "Cuentas de terceros" route additionally requires the `superadmin` role.
//
// Providers (QueryClient, BrowserRouter) live in main.tsx.

import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { useAuthStore } from './stores/auth-store.js'
import AdminLayout from './components/AdminLayout.js'
import { ErrorBoundary } from './components/ErrorBoundary.js'
import { ToastProvider } from './components/Toast.js'
import { ConfirmProvider } from './components/ConfirmDialog.js'
import ComponentsGallery from './pages/dev/ComponentsGallery.js'

import LoginPage from './pages/LoginPage.js'
import SignupPage from './pages/SignupPage.js'
import OnboardingPage from './pages/OnboardingPage.js'
import DashboardPage from './pages/DashboardPage.js'
import CatalogPage from './pages/modules/CatalogPage.js'
import ProductDetailPage from './pages/modules/ProductDetailPage.js'
import CategoryDetailPage from './pages/modules/CategoryDetailPage.js'
import PriceListDetailPage from './pages/modules/PriceListDetailPage.js'
import CatalogModifiersPage from './pages/modules/CatalogModifiersPage.js'
import UnitsCatalogPage from './pages/modules/UnitsCatalogPage.js'
import ModifierDetailPage from './pages/modules/ModifierDetailPage.js'
import CombosPage from './pages/modules/CombosPage.js'
import ComboDetailPage from './pages/modules/ComboDetailPage.js'
import IngredientsPage from './pages/modules/IngredientsPage.js'
import IngredientDetailPage from './pages/modules/IngredientDetailPage.js'
import OrganizationsPage from './pages/modules/OrganizationsPage.js'
import OrganizationDetailPage from './pages/modules/OrganizationDetailPage.js'
import FloorEditorPage from './pages/modules/FloorEditorPage.js'
import ReservationsPage from './pages/modules/ReservationsPage.js'
import ReservationDetailPage from './pages/modules/ReservationDetailPage'
import WaitlistPage from './pages/modules/WaitlistPage.js'
import WaitlistDetailPage from './pages/modules/WaitlistDetailPage'
import UsersPage from './pages/modules/UsersPage.js'
import UserDetailPage from './pages/modules/UserDetailPage.js'
import RolesPage from './pages/modules/RolesPage.js'
import RoleDetailPage from './pages/modules/RoleDetailPage.js'
import DevicesPage from './pages/modules/DevicesPage.js'
import DeviceDetailPage from './pages/modules/DeviceDetailPage'
import FiscalPage from './pages/modules/FiscalPage.js'
import ReportsPage from './pages/modules/ReportsPage.js'
import ReportDetailPage from './pages/modules/ReportDetailPage.js'
import InvoicesPage from './pages/modules/InvoicesPage.js'
import InvoiceDetailPage from './pages/modules/InvoiceDetailPage.js'
import FiscalCustomersPage from './pages/modules/FiscalCustomersPage.js'
import FiscalCustomerDetailPage from './pages/modules/FiscalCustomerDetailPage.js'
import InventoryPage from './pages/modules/InventoryPage.js'
import StockItemDetailPage from './pages/modules/StockItemDetailPage'
import InventoryMovementsPage from './pages/modules/InventoryMovementsPage.js'
import InventoryLotsPage from './pages/modules/InventoryLotsPage.js'
import WarehousesPage from './pages/modules/WarehousesPage.js'
import WarehouseDetailPage from './pages/modules/WarehouseDetailPage.js'
import RecipesPage from './pages/modules/RecipesPage.js'
import RecipeDetailPage from './pages/modules/RecipeDetailPage.js'
import MenusPage from './pages/modules/MenusPage.js'
import MenuDetailPage from './pages/modules/MenuDetailPage'
import FloorQrCodesPage from './pages/modules/FloorQrCodesPage.js'
import CustomersPage from './pages/modules/CustomersPage.js'
import CustomerDetailPage from './pages/modules/CustomerDetailPage'
import LoyaltyConfigPage from './pages/modules/LoyaltyConfigPage.js'
import SuppliersPage from './pages/modules/SuppliersPage.js'
import SupplierDetailPage from './pages/modules/SupplierDetailPage.js'
import PurchaseOrdersPage from './pages/modules/PurchaseOrdersPage.js'
import NewPurchaseOrderPage from './pages/modules/NewPurchaseOrderPage.js'
import PurchaseOrderDetailPage from './pages/modules/PurchaseOrderDetailPage.js'
import PurchaseOrderInboxPage from './pages/modules/PurchaseOrderInboxPage.js'
import SuperadminPage from './pages/modules/SuperadminPage.js'
import ImpersonatePage from './pages/modules/ImpersonatePage.js'
import AuditPage from './pages/modules/AuditPage.js'
import PaymentsSettingsPage from './pages/PaymentsSettingsPage.js'

function RequireAuth({ children }: { children: React.ReactNode }) {
  const session = useAuthStore((s) => s.session)
  const location = useLocation()
  if (!session) {
    const returnTo = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />
  }
  return <>{children}</>
}

function RequireRole({ role, children }: { role: string; children: React.ReactNode }) {
  const session = useAuthStore((s) => s.session)
  if (!session || !session.roles.includes(role)) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <AdminLayout>
        <ErrorBoundary>{children}</ErrorBoundary>
      </AdminLayout>
    </RequireAuth>
  )
}

function HomeRedirect() {
  const session = useAuthStore((s) => s.session)
  return <Navigate to={session ? '/dashboard' : '/login'} replace />
}

export default function App() {
  return (
    <ToastProvider>
    <ConfirmProvider>
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />

      {/* Admin shell */}
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/dashboard" element={<Shell><DashboardPage /></Shell>} />
      <Route path="/catalog" element={<Shell><CatalogPage /></Shell>} />
      <Route path="/catalog/products/:id" element={<Shell><ProductDetailPage /></Shell>} />
      <Route path="/catalog/categories/:id" element={<Shell><CategoryDetailPage /></Shell>} />
      <Route path="/catalog/price-lists/:id" element={<Shell><PriceListDetailPage /></Shell>} />
      <Route path="/catalog/modifiers" element={<Shell><CatalogModifiersPage /></Shell>} />
      {/* Catálogo de unidades — TenantUnit (favoritos SAT + custom) + browse c_ClaveUnidad */}
      <Route path="/catalog/units" element={<Shell><UnitsCatalogPage /></Shell>} />
      <Route path="/catalog/modifiers/:id" element={<Shell><ModifierDetailPage /></Shell>} />
      {/* Combos del catálogo — dhk8-catalog-combos */}
      <Route path="/combos" element={<Shell><CombosPage /></Shell>} />
      <Route path="/combos/:id" element={<Shell><ComboDetailPage /></Shell>} />
      <Route path="/ingredients" element={<Shell><IngredientsPage /></Shell>} />
      <Route path="/ingredients/:id" element={<Shell><IngredientDetailPage /></Shell>} />
      {/* Organizations (multi-branch rollup) — dhk8-platform-tenants */}
      <Route path="/organizations" element={<Shell><RequireRole role="superadmin"><OrganizationsPage /></RequireRole></Shell>} />
      <Route path="/organizations/:id" element={<Shell><RequireRole role="superadmin"><OrganizationDetailPage /></RequireRole></Shell>} />
      {/* Recetario — Flujo ① / ② */}
      <Route path="/recipes" element={<Shell><RecipesPage /></Shell>} />
      <Route path="/recipes/:id" element={<Shell><RecipeDetailPage /></Shell>} />
      <Route path="/menus" element={<Shell><MenusPage /></Shell>} />
      <Route path="/menus/:id" element={<Shell><MenuDetailPage /></Shell>} />
      <Route path="/floor" element={<Shell><FloorEditorPage /></Shell>} />
      <Route path="/floor/qr-codes" element={<Shell><FloorQrCodesPage /></Shell>} />
      <Route path="/reservations" element={<Shell><ReservationsPage /></Shell>} />
      <Route path="/reservations/:id" element={<Shell><ReservationDetailPage /></Shell>} />
      <Route path="/waitlist" element={<Shell><WaitlistPage /></Shell>} />
      <Route path="/waitlist/:id" element={<Shell><WaitlistDetailPage /></Shell>} />
      <Route path="/users" element={<Shell><UsersPage /></Shell>} />
      <Route path="/users/:id" element={<Shell><UserDetailPage /></Shell>} />
      <Route path="/roles" element={<Shell><RolesPage /></Shell>} />
      <Route path="/roles/:id" element={<Shell><RoleDetailPage /></Shell>} />
      <Route path="/devices" element={<Shell><DevicesPage /></Shell>} />
      <Route path="/devices/:id" element={<Shell><DeviceDetailPage /></Shell>} />
      <Route path="/fiscal" element={<Shell><FiscalPage /></Shell>} />
      <Route path="/reports" element={<Shell><ReportsPage /></Shell>} />
      <Route path="/reports/:id" element={<Shell><ReportDetailPage /></Shell>} />
      <Route path="/invoices" element={<Shell><InvoicesPage /></Shell>} />
      <Route path="/invoices/customers" element={<Shell><FiscalCustomersPage /></Shell>} />
      <Route path="/invoices/:id" element={<Shell><InvoiceDetailPage /></Shell>} />
      <Route path="/inventory" element={<Shell><InventoryPage /></Shell>} />
      <Route path="/inventory/movements" element={<Shell><InventoryMovementsPage /></Shell>} />
      <Route path="/inventory/lots" element={<Shell><InventoryLotsPage /></Shell>} />
      <Route path="/inventory/warehouses" element={<Shell><WarehousesPage /></Shell>} />
      <Route path="/inventory/warehouses/:id" element={<Shell><WarehouseDetailPage /></Shell>} />
      <Route path="/inventory/items/:id" element={<Shell><StockItemDetailPage /></Shell>} />
      {/* CRM — contactos para marketing/loyalty */}
      <Route path="/customers" element={<Shell><CustomersPage /></Shell>} />
      <Route path="/customers/crm" element={<Shell><CustomersPage /></Shell>} />
      <Route path="/customers/crm/:id" element={<Shell><CustomerDetailPage /></Shell>} />
      <Route path="/customers/loyalty" element={<Shell><LoyaltyConfigPage /></Shell>} />
      {/* 2026-05-24: clientes fiscales (CFDI) accesibles tanto desde /invoices/customers
          como desde /customers/fiscal — la misma página, dos puntos de entrada para
          que aparezca en la sección "Clientes" del sidebar también */}
      <Route path="/customers/fiscal" element={<Shell><FiscalCustomersPage /></Shell>} />
      <Route path="/customers/fiscal/:id" element={<Shell><FiscalCustomerDetailPage /></Shell>} />
      {/* Compras y Proveedores — Flujo 3 (cross-entity) */}
      <Route path="/suppliers" element={<Shell><SuppliersPage /></Shell>} />
      <Route path="/suppliers/:id" element={<Shell><SupplierDetailPage /></Shell>} />
      <Route path="/purchase-orders" element={<Shell><PurchaseOrdersPage /></Shell>} />
      <Route path="/purchase-orders/new" element={<Shell><NewPurchaseOrderPage /></Shell>} />
      <Route path="/purchase-orders/inbox" element={<Shell><PurchaseOrderInboxPage /></Shell>} />
      <Route path="/purchase-orders/:id" element={<Shell><PurchaseOrderDetailPage /></Shell>} />
      <Route path="/impersonate" element={<Shell><RequireRole role="owner"><ImpersonatePage /></RequireRole></Shell>} />
      <Route path="/superadmin" element={<Shell><RequireRole role="superadmin"><SuperadminPage /></RequireRole></Shell>} />
      {/* Auditoría — registro de cambios del negocio (platform-audit) */}
      <Route path="/audit" element={<Shell><AuditPage /></Shell>} />
      {/* Ajustes — pagos digitales del negocio (dhk8-payments-gateway) */}
      <Route path="/settings/payments" element={<Shell><PaymentsSettingsPage /></Shell>} />

      {/* Galería de componentes (Fase 1 UX/DS) — compuerta de revisión */}
      <Route path="/dev/components" element={<Shell><ComponentsGallery /></Shell>} />

      <Route path="*" element={<HomeRedirect />} />
    </Routes>
    </ConfirmProvider>
    </ToastProvider>
  )
}
